import React from 'react';
import { MdClose, MdSearch } from 'react-icons/md';

import SelectFilter from '../../organisms/SelectFilter';

export interface ToolbarSelectOption {
    value: string;
    label: string;
}

export interface ToolbarSelect {
    id: string;
    label: string;
    value: string;
    options: ToolbarSelectOption[];
    onChange: (value: string) => void;
    /** Largura opcional do select (padrão w-44, igual à tela de monitoramento). */
    className?: string;
}

interface TableToolbarProps {
    search: string;
    onSearchChange: (term: string) => void;
    searchPlaceholder?: string;
    selects?: ToolbarSelect[];
    /** Quantidade após os filtros. */
    filteredCount: number;
    /** Quantidade antes dos filtros. */
    totalCount: number;
    onClear?: () => void;
    hasActiveFilters?: boolean;
    /** Conteúdo extra à direita (ex.: seletor de colunas). */
    trailing?: React.ReactNode;
}

/**
 * AF-3418: barra de filtros das tabelas do recommendation-engine.
 *
 * A tela de recomendações não tinha filtro nenhum — o analista precisava usar o Ctrl+F do
 * navegador, que só encontra o que está renderizado. Com a paginação isso deixaria de
 * funcionar, então busca e filtro passam a ser parte da tela.
 *
 * Usa o SelectFilter da aplicação em vez de <select> nativo. Além de manter o padrão
 * visual, o SelectFilter aplica `appearance-none`: existe uma regra global e sem escopo
 * em molecules/APagination.css que adiciona uma seta de fundo a TODO <select> do app sem
 * suprimir a nativa, então select nativo aparece com duas setas.
 */
const TableToolbar: React.FC<TableToolbarProps> = ({
    search,
    onSearchChange,
    searchPlaceholder = 'Buscar...',
    selects = [],
    filteredCount,
    totalCount,
    onClear,
    hasActiveFilters = false,
    trailing,
}) => (
    // items-end e pt-2, como na barra de filtros do monitoramento: o rótulo do SelectFilter
    // é absolute e sobe acima da borda do select (top-2 com -translate-y-4), então precisa
    // de folga no topo. Com items-center os selects ficavam centralizados em relação à busca
    // e o rótulo era cortado/coberto pelo elemento acima.
    <div className="mb-3 flex flex-wrap items-end gap-3 pt-2">
        <div className="relative min-w-[240px] flex-1">
            <MdSearch
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                size={18}
                aria-hidden="true"
            />
            {/* py-2.5 para casar a altura do SelectFilter, que também usa py-2.5 */}
            <input
                type="search"
                value={search}
                onChange={(event) => onSearchChange(event.target.value)}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                className="block w-full rounded-md border border-gray-300 bg-white py-2.5 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-100"
            />
        </div>

        {selects.map((select) => (
            <SelectFilter
                key={select.id}
                id={select.id}
                label={select.label}
                value={select.value}
                onChange={select.onChange}
                options={select.options}
                className={select.className || 'w-44'}
            />
        ))}

        {hasActiveFilters && onClear && (
            <button
                type="button"
                onClick={onClear}
                className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 dark:border-slate-600 dark:text-gray-200 dark:hover:bg-slate-700"
            >
                <MdClose size={16} aria-hidden="true" />
                Limpar filtros
            </button>
        )}

        <span className="ml-auto whitespace-nowrap pb-2.5 text-sm text-gray-600 dark:text-gray-400">
            {filteredCount === totalCount ? (
                <>
                    <strong className="text-gray-900 dark:text-gray-100">
                        {totalCount.toLocaleString('pt-BR')}
                    </strong>{' '}
                    {totalCount === 1 ? 'documento' : 'documentos'}
                </>
            ) : (
                <>
                    <strong className="text-gray-900 dark:text-gray-100">
                        {filteredCount.toLocaleString('pt-BR')}
                    </strong>{' '}
                    de {totalCount.toLocaleString('pt-BR')}
                </>
            )}
        </span>

        {trailing}
    </div>
);

export default TableToolbar;
